import type { useRouter } from "next/navigation";
import { useAIChatStore } from "@/stores/aiChatStore";
import type { ChatImageAttachment } from "@/types/aiChat";

type Router = ReturnType<typeof useRouter>;

/**
 * Hand off a natural-language prompt to the Caddie panel and navigate into
 * the workflow editor. Shared by the dashboard prompt input, the prompt-idea
 * pills, and any future surface that wants to drop the user into an AI-led
 * build session. Optional images ride along for surfaces that support attach
 * (dashboard input); they're consumed + cleared by the auto-send effect in
 * AIChatStreamPanel.
 *
 * When the user is already on `/workflows/edit` (e.g. PlaceholderNode inside
 * an existing workflow), we skip the navigation entirely so query params like
 * `?workflowId=abc` are preserved and the page doesn't remount.
 */
export function submitPendingPrompt(message: string, router: Router, images?: ChatImageAttachment[]): void {
  const trimmed = message.trim();
  if (!trimmed) return;
  const store = useAIChatStore.getState();
  store.setPendingMessage(trimmed);
  store.setPendingImages(images && images.length > 0 ? images : []);
  store.openPanel();

  // If already on the workflow editor, don't navigate — preserves workflowId
  // and other query params. The panel + pending message are enough.
  const isAlreadyOnEditor = window.location.pathname.endsWith("/workflows/edit");
  if (!isAlreadyOnEditor) {
    router.push("/workflows/edit");
  }
}
